'use client'

import Link from 'next/link'
import { CheckIcon, LockIcon } from 'lucide-react'
import { usePathname, useSearchParams } from 'next/navigation'
import { NEXT_PUBLIC_SHOP_SITE_NAME } from '@/configs/env'
import { parseTenantId, tenantUrl } from '@/lib/tenant'
import useGetTenant from '@/hooks/tenant/use-get-tenant'

const STEPS = ['Đặt hàng', 'Thanh toán', 'Hoàn tất'] as const

// Browsing a product is not a checkout step, so /san-pham shows no breadcrumb at all.
function getCurrentIndex(pathname: string) {
  if (pathname.startsWith('/thanh-toan-thanh-cong')) return 2
  if (pathname.startsWith('/thanh-toan')) return 1
  if (pathname.startsWith('/dat-hang')) return 0
  return -1
}

/**
 * Header + breadcrumb live in the layout, outside any page, so they read the tenant
 * straight from the URL rather than from TenantProvider (which pages own).
 */
export default function SiteChrome() {
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const tenantId = parseTenantId(searchParams.get('tenant_id'))
  const { name: tenantName } = useGetTenant(tenantId)

  // Falls back to the site name only while the tenant lookup is in flight.
  const displayName = tenantName || NEXT_PUBLIC_SHOP_SITE_NAME

  return (
    <>
      <header className="border-rule bg-paper sticky top-0 z-50 border-b">
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex h-16 items-center justify-between gap-4">
            {tenantId ? (
              <Link
                href={tenantUrl('/san-pham', tenantId, { return_url: searchParams.get('return_url') })}
                className="focus-visible:ring-ink min-w-0 rounded focus-visible:ring-2 focus-visible:ring-offset-4 focus-visible:outline-none"
              >
                <Wordmark name={displayName} />
              </Link>
            ) : (
              <Wordmark name={displayName} />
            )}

            {/* The address bar already says "secure"; this says who is on the other
                end of the transaction, which is the part a buyer actually doubts. */}
            <div className="text-ink-faint hidden shrink-0 items-center gap-1.5 text-xs sm:flex">
              <LockIcon className="h-3.5 w-3.5" />
              <span>
                Thanh toán bảo mật qua <span className="text-ink-soft font-semibold">Storims</span>
              </span>
            </div>
          </div>
        </div>
      </header>
      {tenantId && <PurchaseSteps pathname={pathname} tenantId={tenantId} searchParams={searchParams} />}
    </>
  )
}

/**
 * The tenant has no logo to show, so the initial stands in as a stable visual anchor —
 * something the buyer can recognise across steps instead of bare text.
 */
function Wordmark({ name }: { name: string }) {
  const initial = name.trim().charAt(0).toUpperCase() || 'S'

  return (
    <div className="flex min-w-0 items-center gap-2.5">
      <span className="bg-ink flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-sm font-bold text-white">
        {initial}
      </span>
      <span className="text-ink truncate text-lg font-bold tracking-tight">{name}</span>
    </div>
  )
}

function PurchaseSteps({
  pathname,
  tenantId,
  searchParams,
}: {
  pathname: string
  tenantId: string
  searchParams: URLSearchParams
}) {
  const currentIndex = getCurrentIndex(pathname)
  if (currentIndex === -1) return null

  const productId = searchParams.get('product_id')
  const variantId = searchParams.get('variant_id')
  const partnerId = searchParams.get('partner_id')
  const quantity = searchParams.get('quantity')
  const orderId = searchParams.get('order_id')

  const productParams = {
    product_id: productId,
    variant_id: variantId,
    partner_id: partnerId,
    quantity,
    return_url: searchParams.get('return_url'),
    note: searchParams.get('note'),
  }

  const hrefs: (string | null)[] = [
    productId ? tenantUrl('/dat-hang', tenantId, productParams) : null,
    orderId ? tenantUrl('/thanh-toan', tenantId, { order_id: orderId, ...productParams }) : null,
    null,
  ]

  return (
    <div className="border-rule bg-paper sticky top-16 z-40 flex h-12 items-center border-b">
      <div className="mx-auto w-full max-w-7xl px-4">
        <nav aria-label="Tiến trình đặt hàng" className="flex items-center gap-1.5 text-sm sm:gap-3">
          {STEPS.map((label, i) => {
            const isActive = i === currentIndex
            const isDone = i < currentIndex
            const href = hrefs[i]

            // A completed step is a fact worth showing: it tells the buyer what they
            // have already handed over and what is still ahead of them.
            const marker = (
              <span
                className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full font-mono text-[10px] font-bold ${
                  isDone
                    ? 'bg-verify text-white'
                    : isActive
                      ? 'bg-ink text-white'
                      : 'border-rule text-ink-faint border'
                }`}
              >
                {isDone ? <CheckIcon className="h-3 w-3" strokeWidth={3} /> : i + 1}
              </span>
            )

            const text = (
              <span
                className={
                  isActive ? 'text-ink font-semibold' : isDone ? 'text-ink-soft' : 'text-ink-faint hidden sm:inline'
                }
              >
                {label}
              </span>
            )

            return (
              <span key={label} className="flex items-center gap-1.5 sm:gap-3">
                {i > 0 && <span className="bg-rule h-px w-3 sm:w-6" aria-hidden />}
                {isDone && href ? (
                  <Link
                    href={href}
                    className="focus-visible:ring-ink flex items-center gap-1.5 rounded transition-opacity hover:opacity-70 focus-visible:ring-2 focus-visible:outline-none"
                  >
                    {marker}
                    {text}
                  </Link>
                ) : (
                  <span className="flex items-center gap-1.5" aria-current={isActive ? 'step' : undefined}>
                    {marker}
                    {text}
                  </span>
                )}
              </span>
            )
          })}
        </nav>
      </div>
    </div>
  )
}
